import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { completeYahooConnect, isFantasyBackendConfigured } from '../lib/fantasy-api'

type Phase =
  | { kind: 'exchanging' }
  | { kind: 'done'; leagueName: string }
  | { kind: 'pending-backend' }
  | { kind: 'error'; message: string }

const FANTASY_PATH = '/dashboard/fantasy-football'

// Yahoo redirects the browser here after the user approves on yahoo.com. The
// authorization code is single-use and worthless without the client secret, so
// it is relayed straight to the backend, which owns the secret and validates
// the `state` value it issued.
export default function YahooCallbackPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [phase, setPhase] = useState<Phase>({ kind: 'exchanging' })
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return // StrictMode double-invoke would burn the code
    started.current = true

    const error = params.get('error')
    if (error) {
      setPhase({
        kind: 'error',
        message: params.get('error_description') || `Yahoo returned "${error}".`,
      })
      return
    }

    const code = params.get('code')
    if (!code) {
      setPhase({ kind: 'error', message: 'Yahoo did not return an authorization code.' })
      return
    }

    if (!isFantasyBackendConfigured()) {
      setPhase({ kind: 'pending-backend' })
      return
    }

    completeYahooConnect(code, params.get('state'))
      .then((conn) => setPhase({ kind: 'done', leagueName: conn.leagueName }))
      .catch((e) =>
        setPhase({ kind: 'error', message: e instanceof Error ? e.message : 'Token exchange failed.' }),
      )
  }, [params])

  useEffect(() => {
    if (phase.kind !== 'done') return
    const t = setTimeout(() => navigate(FANTASY_PATH, { replace: true }), 1500)
    return () => clearTimeout(t)
  }, [phase, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
        <div className="text-2xl mb-3">🟣</div>
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Yahoo Fantasy
        </h1>

        {phase.kind === 'exchanging' && (
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Finishing the connection…
          </p>
        )}

        {phase.kind === 'done' && (
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Connected <span className="text-slate-900 dark:text-slate-100">{phase.leagueName}</span>.
            Returning to Fantasy Football…
          </p>
        )}

        {phase.kind === 'pending-backend' && (
          <>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Yahoo approved the request and returned an authorization code — this redirect URI is
              wired up correctly.
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              The code cannot be exchanged yet because the fantasy backend is not deployed
              (<code className="text-xs">VITE_FANTASY_API_URL</code> is unset). Nothing was stored.
            </p>
          </>
        )}

        {phase.kind === 'error' && (
          <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">{phase.message}</p>
        )}

        <button
          onClick={() => navigate(FANTASY_PATH, { replace: true })}
          className="mt-5 w-full rounded-lg border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          Back to Fantasy Football
        </button>
      </div>
    </div>
  )
}
